import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Heading,
  useToast,
  Spinner,
  Checkbox,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import ApiResponse from "../../../services/ApiResponse";
import ProtectedRoute from "../../../services/ProtectedRoute";
import { getDecodedToken } from "../../../services/Token";
import { Evento, EventoCreate } from "../../../services/models/Evento";
import { ApiValidationResponse } from "../../../services/models/ApiValidationResponse";
import handleApiError from "../../../utils/ApiErrorHandler";


interface Tipo {
  id: number;
  nombre: string;
}

interface Institucion {
  id: number;
  nombre: string;
}

const EventoAddPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const decoded = getDecodedToken();

  // Estados
  const [loading, setLoading] = useState(false);
  const [tipos, setTipos] = useState<Tipo[]>([]);
  const [loadingTipos, setLoadingTipos] = useState(true);
  const [instituciones, setInstituciones] = useState<Institucion[]>([]);
  const [loadingInstituciones, setLoadingInstituciones] = useState(true);

  // Refs
  const tituloRef = useRef<HTMLInputElement>(null);
  const organizadorRef = useRef<HTMLInputElement>(null);
  const institucionRef = useRef<HTMLSelectElement>(null);
  const ubicacionRef = useRef<HTMLInputElement>(null);
  const descripcionRef = useRef<HTMLTextAreaElement>(null);
  const inicioRef = useRef<HTMLInputElement>(null);
  const finRef = useRef<HTMLInputElement>(null);
  const esRecurrenteRef = useRef<HTMLInputElement>(null);
  const tipoRef = useRef<HTMLSelectElement>(null);

  const [selectedTipo, setSelectedTipo] = useState<number | null>(null);
  const [sinTipoId, setSinTipoId] = useState<number | null>(null);
  // Cargar tipos al montar
  useEffect(() => {
    const fetchTipos = async () => {
      try {
        const response = new ApiResponse<Tipo[]>();
        await response.useFetch("tipos", "GET");
  
        if (!response.data) return;
  
        const educativo = response.data.find(
          (t) => t.nombre.toLowerCase() === "educativo"
        );
  
        const sinTipo = response.data.find(
          (t) => t.nombre.toLowerCase() === "sin tipo"
        );
  
        if (sinTipo) setSinTipoId(sinTipo.id);
  
        // Quitamos "Sin tipo" del combo
        setTipos(response.data.filter((t) => t.id !== sinTipo?.id));
  
        // Educativo por defecto
        if (educativo) {
          setSelectedTipo(educativo.id);
        }
      } catch (error) {
        console.error("Error cargando tipos:", error);
      } finally {
        setLoadingTipos(false);
      }
    };
  
    fetchTipos();
  }, []);

  // Cargar instituciones al montar
  useEffect(() => {
    const fetchInstituciones = async () => {
      try {
        const response = new ApiResponse<Institucion[]>();
        await response.useFetch("instituciones", "GET");
        if (response.data) {
          setInstituciones(response.data);
        }
      } catch (error) {
        console.error("Error cargando instituciones:", error);
      } finally {
        setLoadingInstituciones(false);
      }
    };

    fetchInstituciones();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validaciones
    if (!tituloRef.current?.value) {
      toast({
        title: "Error de validación",
        description: "El título del evento es requerido",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (inicioRef.current?.value === "" || finRef.current?.value === "") {
      toast({
        title: "Error de validación",
        description: "Debes agregar al menos un horario con fecha de inicio y fin",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);

    try {
      // Preparar datos del evento
      const eventoData: EventoCreate = {
        titulo: tituloRef.current.value,
        descripcion: descripcionRef.current?.value || "",
        organizador: decoded?.usuario_id || 0,
        institucion_id:
          decoded?.rol_id === 1
            ? institucionRef.current?.value
              ? parseInt(institucionRef.current.value)
              : undefined
            : decoded?.institucion_id,
        ubicacion: ubicacionRef.current?.value || undefined,
      };
      // Crear evento
      const eventoResponse = new ApiResponse<Evento>();
      await eventoResponse.useFetch("eventos", "POST", eventoData);

      if (eventoResponse.responseCode !== 201) {
        throw new Error(eventoResponse.error || "Error al crear evento");
      }

      const eventoId = eventoResponse.data?.id;

      // Guardar horarios si el evento se creó
      if (eventoId && inicioRef.current && finRef.current) {
          const horarioData = {
            evento_id: eventoId,
            inicio: inicioRef.current.value,
            fin: finRef.current.value,
            esRecurrente: esRecurrenteRef.current?.checked ?? false,
            rrule: null,
          };
          const horarioResponse = new ApiResponse();
          await horarioResponse.useFetch("horarios", "POST", horarioData);

          if (horarioResponse.responseCode !== 201) {
            if (
              typeof horarioResponse.error === "object" &&
              horarioResponse.error !== null &&
              "errors" in horarioResponse.error
            ) {
              handleApiError(horarioResponse.error as ApiValidationResponse, toast);
              return;
            } else {
              toast({
                title: "Error al Guardar el Horario.",
                description:
                  horarioResponse.error ||
                  "Ocurrió un problema al guardar el horario.",
                status: "error",
                duration: 5000,
                isClosable: true,
              });
            }
          }
        }
      

      // Guardar tipo si se seleccionó
      if (eventoId && selectedTipo) {
        console.log(selectedTipo);
        const eventoTipoData = {
          evento_id: eventoId,
          tipo_id: selectedTipo,
        };
        
        console.log("data con tipo" + eventoTipoData);
        const tipoResponse = new ApiResponse();
        await tipoResponse.useFetch("evento-tipos", "POST", eventoTipoData);

        if (tipoResponse.responseCode !== 201) {
          throw new Error(tipoResponse.error || "Error al asignar tipo");
        }
      }
      else if(eventoId && !selectedTipo)
      {
        const eventoTipoData = {
          evento_id: eventoId,
          tipo_id: sinTipoId,
        };
        console.log(sinTipoId);
        
        console.log("data sin tipo" + eventoTipoData.tipo_id);
        
        const tipoResponse = new ApiResponse();
        await tipoResponse.useFetch("evento-tipos", "POST", eventoTipoData);

        if (tipoResponse.responseCode !== 201) {
          throw new Error(tipoResponse.error || "Error al asignar tipo");
        }
      }

      toast({
        title: "Éxito",
        description: "Evento creado correctamente",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      navigate("/dashboard");
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Error al crear el evento",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  if (decoded?.rol_id !== 1 && decoded?.rol_id !== 2) {
    return (
      <Box p={5} bg="rgba(2, 54, 176, 0.9)" minH="100vh">
        <Heading color="white">No tienes permiso para acceder aquí</Heading>
      </Box>
    );
  }

  return (
    <ProtectedRoute roles={[1, 2]}>
      <Box p={5} bg="rgba(2, 54, 176, 0.9)" minH="100vh">
        <VStack spacing={6} maxW="1000px" mx="auto" align="stretch">
          <Heading color="white" textAlign="center">
            Crear Nuevo Evento
          </Heading>

          <Box bg="whiteAlpha.100" p={6} borderRadius="lg">
            <form onSubmit={handleSubmit}>
              <VStack spacing={4}>
                {/* Título */}
                <FormControl isRequired>
                  <FormLabel color="white">Título del Evento</FormLabel>
                  <Input
                    ref={tituloRef}
                    placeholder="Ej: Feria de Ciencias 2025"
                    bg="white"
                    color="black"
                  />
                </FormControl>

                {/* Descripción */}
                <FormControl>
                  <FormLabel color="white">Descripción</FormLabel>
                  <Textarea
                    name="descripcion"
                    ref={descripcionRef}
                    placeholder="Describe el evento en detalle..."
                    bg="white"
                    color="black"
                    rows={6}
                  />
                </FormControl>
                
                {/* Ubicación */}
                <FormControl>
                  <FormLabel color="white">Ubicación</FormLabel>
                  <Input
                    ref={ubicacionRef}
                    placeholder="Ej: Auditorio Principal"
                    bg="white"
                    color="black"
                  />
                </FormControl>

                {/* Institución (solo para SuperUser) */}
                {decoded?.rol_id === 1 && (
                  <FormControl>
                    <FormLabel color="white">Institución</FormLabel>
                    {loadingInstituciones ? (
                      <Spinner color="white" />
                    ) : (
                      <Select ref={institucionRef} bg="white" color="black">
                        <option value="">Sin institución</option>
                        {instituciones.map((institucion) => (
                          <option key={institucion.id} value={institucion.id}>
                            {institucion.nombre}
                          </option>
                        ))}
                      </Select>
                    )}
                  </FormControl>
                )}

                {/* Horarios */}
                <FormControl isRequired>
                  <FormLabel color="white">Horarios</FormLabel>
                  <VStack spacing={3} align="stretch">
                      <Box
                        bg="whiteAlpha.200"
                        p={4}
                        borderRadius="md"
                        border="1px solid rgba(255,255,255,0.3)"
                      >
                        <HStack spacing={3} mb={3}>
                          <FormControl>
                            <FormLabel color="white" fontSize="sm">
                              Inicio
                            </FormLabel>
                            <Input
                              type="datetime-local"
                              ref={inicioRef}
                              bg="white"
                              color="black"
                            />
                          </FormControl>

                          <FormControl>
                            <FormLabel color="white" fontSize="sm">
                              Fin
                            </FormLabel>
                            <Input
                              type="datetime-local"
                              ref={finRef}
                              bg="white"
                              color="black"
                            />
                          </FormControl>
                        </HStack>

                        <FormControl display="flex" alignItems="center">
                          <Checkbox
                            isChecked={false}
                            ref={esRecurrenteRef}
                            colorScheme="blue"
                          />
                          <FormLabel color="white" fontSize="sm" mb={0} ml={2}>
                            Es recurrente
                          </FormLabel>
                        </FormControl>
                      </Box>
                  </VStack>
                </FormControl>

                {/* Tipos de eventos */}
                                      <FormControl isRequired>
                        <FormLabel color="white">Categoría del Evento</FormLabel>
                        {loadingTipos ? (
                          <Spinner color="white" />
                        ) : (
                          <Select
                            value={selectedTipo ?? ""}
                            onChange={(e) =>
                              setSelectedTipo(e.target.value ? Number(e.target.value) : null)
                            }
                            bg="white"
                            color="black"
                          >
                            {tipos.map((tipo) => (
                              <option key={tipo.id} value={tipo.id}>
                                {tipo.nombre}
                              </option>
                            ))}
                          </Select>
                        )}
                      </FormControl>

                {/* Botones */}
                <HStack spacing={4} pt={4}>
                  <Button
                    colorScheme="blue"
                    type="submit"
                    isLoading={loading}
                    width="50%"
                  >
                    Crear Evento
                  </Button>
                  <Button
                    colorScheme="gray"
                    onClick={() => navigate(-1)}
                    width="50%"
                  >
                    Cancelar
                  </Button>
                </HStack>
              </VStack>
            </form>
          </Box>
        </VStack>
      </Box>
    </ProtectedRoute>
  );
};

export default EventoAddPage;
